import { z } from 'zod';

// Helper sub-schemas for commonly used types in vis.js node options.
export const ColorSchema = z.union([
	z.string(),
	z.object({
		background: z.string().optional(),
		border: z.string().optional(),
		highlight: z
			.object({
				background: z.string().optional(),
				border: z.string().optional(),
			})
			.optional(),
	}),
]);

export const FontSchema = z.union([
	z.string(),
	z
		.object({
			color: z.string().optional(),
			size: z.number().optional(),
			face: z.string().optional(),
			vadjust: z.number().optional(),
			multi: z.boolean().optional(),
		})
		.partial(),
]);

export const ImageSchema = z.union([
	z.string(),
	z
		.object({
			url: z.string().optional(),
			selected: z.string().optional(),
			unselected: z.string().optional(),
			size: z.number().optional(),
		})
		.partial(),
]);

export const ImagePaddingSchema = z.union([
	z.number(),
	z
		.object({
			top: z.number().optional(),
			right: z.number().optional(),
			bottom: z.number().optional(),
			left: z.number().optional(),
		})
		.partial(),
]);

export const OptionsScalingSchema = z
	.object({
		min: z.number().optional(),
		max: z.number().optional(),
		label: z.boolean().optional(),
		maxVisible: z.number().optional(),
	})
	.partial();

export const OptionsShadowSchema = z.union([
	z.boolean(),
	z
		.object({
			color: z.string().optional(),
			size: z.number().optional(),
			x: z.number().optional(),
			y: z.number().optional(),
			enabled: z.boolean().optional(),
		})
		.partial(),
]);

export const NodeChosenSchema = z
	.object({ node: z.boolean().optional() })
	.partial();

/**
 * Zod schema mirroring vis.js `NodeOptions`.
 * Only includes a subset of exact shapes but is permissive where web types are used.
 */
export const Node = z.object({
	id: z.string(),
	borderWidth: z.number().optional(),
	borderWidthSelected: z.number().optional(),
	brokenImage: z.string().optional(),
	color: z.union([z.string(), ColorSchema]).optional(),
	chosen: z.union([z.boolean(), NodeChosenSchema]).optional(),
	opacity: z.number().optional(),
	fixed: z
		.union([
			z.boolean(),
			z
				.object({ x: z.boolean().optional(), y: z.boolean().optional() })
				.partial(),
		])
		.optional(),
	font: z.union([z.string(), FontSchema]).optional(),
	group: z.string().optional(),
	hidden: z.boolean().optional(),
	icon: z
		.object({
			face: z.string().optional(),
			code: z.string().optional(),
			size: z.number().optional(),
			color: z.string().optional(),
			weight: z.union([z.number(), z.string()]).optional(),
		})
		.partial()
		.optional(),
	image: ImageSchema.optional(),
	imagePadding: ImagePaddingSchema.optional(),
	label: z.string().optional(),
	labelHighlightBold: z.boolean().optional(),
	level: z.number().optional(),
	margin: z
		.object({
			top: z.number().optional(),
			right: z.number().optional(),
			bottom: z.number().optional(),
			left: z.number().optional(),
		})
		.partial()
		.optional(),
	mass: z.number().optional(),
	physics: z.boolean().optional(),
	scaling: OptionsScalingSchema.optional(),
	shadow: z.union([z.boolean(), OptionsShadowSchema]).optional(),
	shape: z.string().optional(),
	shapeProperties: z
		.object({
			borderDashes: z.union([z.boolean(), z.array(z.number())]).optional(),
			borderRadius: z.number().optional(),
			interpolation: z.boolean().optional(),
			useImageSize: z.boolean().optional(),
			useBorderWithImage: z.boolean().optional(),
			coordinateOrigin: z.string().optional(),
		})
		.partial()
		.optional(),
	size: z.number().optional(),
	title: z.string().optional(),
	value: z.number().optional(),
	widthConstraint: z
		.union([
			z.number(),
			z.boolean(),
			z
				.object({
					minimum: z.number().optional(),
					maximum: z.number().optional(),
				})
				.partial(),
		])
		.optional(),
	x: z.number().optional(),
	y: z.number().optional(),
});

export type Node = z.infer<typeof Node>;

export default Node;

/**
 * FamilyNode extends the generic vis.js Node options with family-specific metadata.
 */
export const FamilyNodeSchema = Node.extend({
	personId: z.string().optional(),
	firstName: z.string().optional(),
	lastName: z.string().optional(),
	birthDate: z.string().optional(),
	deathDate: z.string().optional(),
	gender: z.union([z.literal('M'), z.literal('F')]).optional(),
});

export type FamilyNode = z.infer<typeof FamilyNodeSchema>;

// IdType used by vis-network: number | string
export const IdType = z.union([z.string(), z.number()]);
export type IdType = z.infer<typeof IdType>;

// Edge options schema (subset, permissive)
export const EdgeOptionsSchema = z
	.object({
		label: z.string().nullable(),
		title: z.string().nullable(),
		hidden: z.boolean().nullable(),
		physics: z.boolean().nullable(),
		dashes: z.union([z.boolean(), z.array(z.number())]).nullable(),
		arrows: z
			.union([
				z.string(),
				z
					.object({
						to: z.object({ enabled: z.boolean().optional(), type: z.string().optional(), scaleFactor: z.number().optional() }).partial().nullable(),
						from: z.object({ enabled: z.boolean().optional(), type: z.string().optional(), scaleFactor: z.number().optional() }).partial().nullable(),
					})
					.partial(),
			])
			.nullable(),
		color: z.union([z.string(), ColorSchema]).nullable(),
		width: z.number().nullable(),
		length: z.number().nullable(),
		smooth: z
			.union([
				z.boolean(),
				z
					.object({
						enabled: z.boolean().nullable(),
						type: z.string().nullable(),
					})
					.partial(),
			])
			.nullable(),
		font: FontSchema.nullable(),
		value: z.number().nullable(),
		// allow arbitrary extra options
	})
	.partial();

// Edge schema: includes from/to/id as IdType
export const EdgeSchema = EdgeOptionsSchema.extend({
	from: IdType.optional(),
	to: IdType.optional(),
	id: IdType.optional(),
});

export type Edge = z.infer<typeof EdgeSchema>;
